import { FeatureFlag, Size } from '@ps/web-core-enums';
import { StarsButton, Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import CoachMark from '@swf/components/CoachMark/CoachMark';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { DropdownMenu, useCheckUserClubRole, useHomeGamesWidget } from '@swf/poker';

import { HGOnboarding, ModalRoutes, UserClubRole } from '../../../../types/common';
import Reminder from '../../../CoachMark/CreateGame/Reminder/Reminder';
import type { MenuOption } from '../../../DropdownMenu/DropdownMenu';
import AddIcon from '../../../assets/plus.svg';

import {
  TXTCLI_Create_Tournament,
  TXTMOB_Create,
  TXTWCL_Create_Cash_Game
} from './CreateGameButtons.messages.json';
import styles from './CreateGameButtons.scss';

export const options = (isCashGamesActive: boolean, isTournsActive: boolean): MenuOption[] => [
  {
    text: <Translation {...TXTWCL_Create_Cash_Game} />,
    value: ModalRoutes.createCashGame,
    data: TXTWCL_Create_Cash_Game,
    isActive: isCashGamesActive
  },
  {
    text: <Translation {...TXTCLI_Create_Tournament} />,
    value: ModalRoutes.createTournament,
    data: TXTCLI_Create_Tournament,
    isActive: isTournsActive
  }
];

const CreateGameButtons: React.FC = () => {
  const { widgetSelect, widgetDispatch, widgetId } = useHomeGamesWidget();
  const triggerRef = useRef<HTMLDivElement>(null);
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { modal } = useDispatch<Dispatch>();
  const { greaterThan } = useScreenSize();
  const isNativeMobile = useSelector(select.device.isMobileEmbedded);

  const isManager = useCheckUserClubRole(clubId, [UserClubRole.Manager]);
  const createGameOBEnabled = useFeature(FeatureFlag.PKRHomeGamesOnboardingCreateGame);
  const createdGame = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `onboarding[${HGOnboarding.CreatedGame}]`)
  );
  const newClubCreateGameOnboardingCompleted = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(rootState, `onboarding[${HGOnboarding.NewClubCreateGame}]`)
  );
  const newClubCreateGameReminderCompleted = useSelector((rootState: RootState) =>
    widgetSelect.localStorageItem(
      rootState,
      `onboarding[${HGOnboarding.NewClubCreateGameReminder}]`
    )
  );
  const cashGames = useSelector(widgetSelect.tables);
  const tourns = useSelector(widgetSelect.tournaments);
  const noGames = isEmpty(cashGames) && isEmpty(tourns);

  const isCashGamesActive = useSelector(widgetSelect.isCashGameActive);
  const isTournsActive = useSelector(widgetSelect.isTournsActive);

  const showCoachMark =
    createGameOBEnabled &&
    isManager &&
    newClubCreateGameOnboardingCompleted &&
    createdGame &&
    !noGames &&
    !newClubCreateGameReminderCompleted;

  const handleSelect = ({ value }: MenuOption) => {
    if (!newClubCreateGameOnboardingCompleted) {
      widgetDispatch.setLocalStorageItem({
        onboarding: { [HGOnboarding.NewClubCreateGame]: true }
      });
    }

    if (!newClubCreateGameReminderCompleted) {
      widgetDispatch.setLocalStorageItem({
        onboarding: { [HGOnboarding.NewClubCreateGameReminder]: true }
      });
    }

    modal.enqueue({
      path: `${value}`,
      locationState: { clubId: Number(clubId), widgetId, widgetDispatch, widgetSelect }
    });
  };

  const handleCoachMarkDismiss = () =>
    widgetDispatch.setLocalStorageItem({
      onboarding: { [HGOnboarding.NewClubCreateGameReminder]: true }
    });

  return (
    <div
      ref={triggerRef}
      className={classNames(styles.container, {
        [styles.containerMobile]: isNativeMobile
      })}
    >
      {showCoachMark ? (
        <CoachMark content={<Reminder onDismiss={handleCoachMarkDismiss} />} placement="bottom">
          <DropdownMenu
            onSelect={handleSelect}
            options={options(isCashGamesActive, isTournsActive)}
          >
            <StarsButton
              className={styles.button}
              translation={greaterThan(Size.Small) ? TXTMOB_Create : undefined}
              icon={<AddIcon className={styles.icon} />}
              onClick={handleCoachMarkDismiss}
            />
          </DropdownMenu>
        </CoachMark>
      ) : (
        <DropdownMenu onSelect={handleSelect} options={options(isCashGamesActive, isTournsActive)}>
          <StarsButton
            className={styles.button}
            translation={greaterThan(Size.Small) ? TXTMOB_Create : undefined}
            icon={<AddIcon className={styles.icon} />}
          />
        </DropdownMenu>
      )}
    </div>
  );
};

export default CreateGameButtons;
